import { useMemo } from 'react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

import type { ViewCommandId, ViewCommandRegistry } from '../../view';
import { useInternalCommandPaletteActions } from '../components/command-palette-state-provider';
import { useCommandRegistry } from '../components/command-registry-provider';

/**
 * Public action surface. Identity is stable across the common churn
 * (typing, command registration) and changes only when the palette mode
 * transitions. Safe to use as an effect dep -- such an effect re-runs at
 * most on a mode transition, never on a keystroke.
 *
 * Three actions: open the palette, close it, and open a view directly.
 *
 *   1. Consumers declare commands via `useCommands(...)`.
 *   2. Users activate commands by typing + Enter, clicking the row,
 *      pressing a per-command shortcut, or typing a snap prefix.
 *   3. The palette handles entering roots, opening views, navigating
 *      links, running action callbacks.
 *
 * `openView` is the one sanctioned programmatic entry point, and it's
 * scoped to *views only* — actions/links/roots keep the "declare +
 * activate" model untouched. A view isn't a position in a list; it's
 * a focused, self-contained task surface, and "summon directly into
 * this task" is a first-class need (a contextual button like the
 * shortcuts trigger, an AI router routing "give feedback about X" with
 * prefill, a deep link). It takes a command *id*, not a command
 * reference — see {@link ViewCommandRegistry} for why.
 */
export interface CommandPaletteActions {
  /**
   * Open the palette. `trigger` discriminates the entry surface for
   * analytics (`'button'` for click-driven opens, `'keyboard'` for
   * the global shortcut).
   */
  openPalette: (trigger: 'button' | 'keyboard') => void;
  /** Close the palette, whether currently in list or view mode. */
  closePalette: () => void;
  /**
   * Open the palette directly into a registered view command, by id.
   *
   * Typed by id: `id` is constrained to the ids app-land registered in
   * {@link ViewCommandRegistry}, and `prefill` is checked against that
   * view's declared payload type. The argument is always optional
   * because every view can also be activated from the command list,
   * where no programmatic payload exists.
   *
   * Resolves the id against the live registry and delegates to the
   * internal view-open path, which validates `prefill` against the
   * command's `prefillSchema` before dispatching. If the id isn't
   * currently registered (e.g. its flag is off or the route doesn't
   * contribute it) or doesn't resolve to a view command, the call is a
   * no-op — which mirrors the gating of any button that would invoke it.
   */
  openView: <K extends ViewCommandId>(id: K, prefill?: ViewCommandRegistry[K]) => void;
}

/**
 * Hook returning the public action surface. Subscribes to the stable
 * `CommandPaletteActionsContext` (so calling components never re-render
 * on palette state changes) and reads the command registry through a
 * ref — resolving the id at call time keeps the returned actions object
 * identity stable even as commands register and unregister.
 */
export function useCommandPaletteActions(): CommandPaletteActions {
  const internal = useInternalCommandPaletteActions();
  const { commands } = useCommandRegistry();
  // Read the registry at call time via a ref so the actions object
  // stays referentially stable (commands churn as sources publish);
  // capturing `commands` in the memo deps would bust that stability.
  const commandsRef = useLatestRef(commands);

  return useMemo<CommandPaletteActions>(
    () => ({
      openPalette: internal.open,
      closePalette: internal.close,
      openView: (id, prefill) => {
        const command = commandsRef.current[id];
        if (command === undefined || command.type !== 'view') {
          // Not currently registered (flag/route gating), or the id
          // resolves to a non-view command: no-op.
          return;
        }
        // `internal.openView` validates prefill against the command's
        // prefillSchema, emits analytics, and dispatches. Its signature
        // is intentionally loose (ViewCommand<unknown>, prefill?), so
        // the id-keyed public arg flows in without a cast; the typed
        // guarantee lives on this hook's public signature above.
        internal.openView(command, prefill);
      },
    }),
    [internal, commandsRef],
  );
}
